import { Link } from '#app/components/Link/index.js';
import { Logo } from '#app/components/Logo/src/Logo.js';
import { useSidebar } from '#app/contexts/SidebarContext/useSidebar.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useCurrentUser } from '#app/hooks/useCurrentUser.js';
import { useIsMobile } from '#app/hooks/useIsMobile.js';
import { useLastLocation } from '#app/hooks/useLastLocation.js';
import { useLocation } from '#app/hooks/useLocation.js';
import { usePageContext } from '#app/hooks/usePageContext.js';
import { useSelectedUserType } from '#app/hooks/useSelectedUserType.js';
import { type AppBarProps } from '../AppBar.js';
import { Nav } from '../AppBar.styles.js';
import {
  getAuthenticatedRouteLabel,
  type GetAuthenticatedRouteLabelPayload,
} from '../utilities/getAuthenticatedRouteLabel.js';
import { MobileMenuButton } from './MobileMenuButton.js';
import { NavbarSpacer } from './NavbarSpacer.js';
import { lazy } from 'react';
import { css, styled } from 'styled-components';

export type AppBarLeftProps = Pick<AppBarProps, 'username'> & {
  readonly routeLabelOverride?: GetAuthenticatedRouteLabelPayload;
};

const BackToProfile = lazy(async () => {
  return {
    default: (await import('#app/components/BackToProfile/BackToProfile.js'))
      .BackToProfile,
  };
});

const StaticRouteLabel = lazy(async () => {
  return {
    default: (await import('./StaticRouteLabel.js')).StaticRouteLabel,
  };
});

const LogoLink = styled(Link)`
  ${({ theme }) => css`
    border-radius: 8px;
    padding: 4px;
    transition: background-color 0.2s ease-in-out;
    :hover,
    :active,
    :focus {
      background: ${theme.colorsV2.gray20};
    }
  `}
`;

export const AppBarLeft = ({
  routeLabelOverride,
  username,
}: AppBarLeftProps) => {
  const currentUser = useCurrentUser();
  const isLoggedIn = Boolean(currentUser);
  const isMobile = useIsMobile();
  const { track } = useAnalytics();
  const { routeHelpers } = useSelectedUserType();
  const pageContext = usePageContext();
  const location = useLocation();
  const sidebar = useSidebar();
  const lastLocation = useLastLocation();

  const routeLabel =
    routeLabelOverride ??
    getAuthenticatedRouteLabel({
      contextualUsername: username,
      lastRoute: lastLocation?.pathname ?? null,
      location,
      pathname: pageContext.urlParsed.pathname,
      selectedUserTypeRouteBase: routeHelpers?.selectedUserTypeRouteBase,
    });

  const resolveLabelComponent = ({
    label,
    size,
    to,
  }: GetAuthenticatedRouteLabelPayload) => {
    if (label === 'Logo') {
      return (
        <LogoLink
          aria-label="to home"
          onClick={() =>
            track('navigation_item', {
              navigation_item_name: 'home',
            })
          }
          role="banner"
          to="/"
        >
          <Logo
            condensed={isMobile}
            variant="black"
          />
        </LogoLink>
      );
    }

    if (to) {
      return (
        <BackToProfile
          to={to}
          variant={size}
        >
          {label}
        </BackToProfile>
      );
    }

    return <StaticRouteLabel>{label}</StaticRouteLabel>;
  };

  /**
   * If we're unauthenticated, we always show the Contra logo on the left side.
   * See: https://www.figma.com/file/8DGsHzynsrN2YyH59RJKu6/V4-Library?type=design&node-id=3-9&mode=design&t=GW9naXYKNqzKar9H-0
   */
  if (!isLoggedIn) {
    return (
      <Nav.Left>
        <a
          aria-label="to home"
          href="https://contra.com"
          onClick={() =>
            track('navigation_item', {
              navigation_item_name: 'home',
            })
          }
        >
          <Logo
            condensed={isMobile}
            variant="black"
          />
        </a>
      </Nav.Left>
    );
  }

  if (isMobile) {
    return (
      <>
        <Nav.Left>
          <MobileMenuButton
            isMenuOpen={false}
            toggleMenu={() => sidebar.mobile?.open()}
          />
        </Nav.Left>

        <Nav.Center>
          <LogoLink
            aria-label="to home"
            onClick={() =>
              track('navigation_item', {
                navigation_item_name: 'home',
              })
            }
            role="banner"
            to="/"
          >
            <Logo variant="black" />
          </LogoLink>
        </Nav.Center>
      </>
    );
  }

  return (
    <Nav.Left>
      <NavbarSpacer open={sidebar.desktop?.isExpanded ?? false} />
      {resolveLabelComponent(routeLabel)}
    </Nav.Left>
  );
};
